import { Classes, Intent } from "@blueprintjs/core";
import {
  OrgRole,
  OrgRoleRequestGatedAction,
  OrgRoleRequestSource,
} from "@hex/common";
import React, { memo, useMemo } from "react";
import styled from "styled-components";

import {
  HexButton,
  HexMenu,
  HexMenuDivider,
  HexPopover,
} from "../../hex-components/index.js";
import ProjectsGateIcon from "../../static/illustration/illustration-featuregate-projects.inline.svg";
import { HotKeys } from "../../util/hotkeys.js";
import { TestSelectors } from "../../util/testSelectors.js";
import { HotkeyHelperTooltip } from "../common/HotkeyHelperTooltip.js";
import { FeatureGateToolTip } from "../feature-gate/FeatureGateToolTip";
import { AddIcon, ProjectIcon, ThreadIcon } from "../icons/CustomIcons.js";

import {
  CreateCollectionMenuItem,
  CreateComponentMenuItem,
  CreateExplorationMenuItem,
  CreateRComponentMenuItem,
  CreateRProjectMenuItem,
  ImportComponentMenuItem,
  ImportProjectMenuItem,
} from "./create-items/CreateMenuItems";
import {
  useCreateProjectButton,
  useCreateThreadButton,
} from "./create-items/useCreateAssets";
import { RoleRequestTooltip } from "./RoleRequestTooltip";

interface QuickCreateButtonsProps {
  location: "home_sidebar" | "popover_sidebar";
}

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: stretch;
`;

const ButtonWrapper = styled.div`
  flex: 1;

  .${Classes.POPOVER_TARGET} {
    width: 100%;
  }
`;

const MoreButton = styled(HexButton)`
  flex: 0;
`;

export const QuickCreateButtons = memo(function QuickCreateButtons({
  location,
}: QuickCreateButtonsProps) {
  const {
    canCreateNewThread,
    createNewThread,
    disabledForRole: createThreadDisabledForRole,
    hasFeatureGate: createThreadFeatureGate,
    minimumRoleForAccess,
  } = useCreateThreadButton({
    location,
  });
  const {
    createNewProject,
    disabledForRole: createProjectDisabledForRole,
    newProjectDisabled,
    projectLimit,
    projectLimitEnforced,
  } = useCreateProjectButton({
    location,
  });

  const dropdownMenuContents = useMemo(() => {
    const placement = "right-start";
    return (
      <HexMenu>
        <CreateComponentMenuItem location={location} placement={placement} />
        <CreateExplorationMenuItem location={location} placement={placement} />
        <CreateCollectionMenuItem location={location} placement={placement} />
        <HexMenuDivider />
        <CreateRProjectMenuItem location={location} placement={placement} />
        <CreateRComponentMenuItem location={location} placement={placement} />
        <HexMenuDivider />
        <ImportProjectMenuItem location={location} placement={placement} />
        <ImportComponentMenuItem location={location} placement={placement} />
      </HexMenu>
    );
  }, [location]);

  return (
    <ButtonsContainer>
      <ButtonWrapper>
        <HotkeyHelperTooltip
          combo={HotKeys.NEW_PROJECT}
          css="flex: 1;"
          disabled={newProjectDisabled}
          hotkeyName="New project"
          placement="bottom"
        >
          <FeatureGateToolTip
            descriptionOverride="Unlock unlimited projects with a paid plan."
            disabled={!projectLimitEnforced}
            featureGate="projectLimit"
            icon={<ProjectsGateIcon />}
            placement="bottom-start"
            titleOverride={`Your workspace has reached the ${projectLimit} project limit.`}
          >
            <RoleRequestTooltip
              contentText="You do not have permission to create a project."
              disabled={!createProjectDisabledForRole || !!projectLimitEnforced}
              gatedAction={OrgRoleRequestGatedAction.CREATE_PROJECT}
              placement="bottom"
              requestSource={OrgRoleRequestSource.CREATE_PROJECT}
              role={OrgRole.EDITOR}
            >
              <HexButton
                data-test={TestSelectors.CREATE_PROJECT}
                disabled={newProjectDisabled}
                fill={true}
                icon={<ProjectIcon />}
                intent={Intent.NONE}
                onClick={createNewProject}
              >
                Project
              </HexButton>
            </RoleRequestTooltip>
          </FeatureGateToolTip>
        </HotkeyHelperTooltip>
      </ButtonWrapper>
      <ButtonWrapper>
        <FeatureGateToolTip
          action="create a new thread"
          disabled={createThreadFeatureGate}
          featureGate="exploreFromScratch"
          placement="bottom-start"
        >
          <RoleRequestTooltip
            contentText="You do not have permission to create a thread."
            disabled={!createThreadDisabledForRole || !createThreadFeatureGate}
            gatedAction={OrgRoleRequestGatedAction.CREATE_THREAD}
            placement="bottom"
            requestSource={OrgRoleRequestSource.CREATE_PROJECT}
            role={minimumRoleForAccess}
          >
            <HexButton
              data-test={TestSelectors.CREATE_THREAD}
              disabled={!canCreateNewThread}
              fill={true}
              icon={<ThreadIcon />}
              intent={Intent.NONE}
              onClick={createNewThread}
            >
              Thread
            </HexButton>
          </RoleRequestTooltip>
        </FeatureGateToolTip>
      </ButtonWrapper>
      <HexPopover
        content={dropdownMenuContents}
        minimal={true}
        placement="bottom-start"
      >
        <MoreButton
          data-test={TestSelectors.NEW_PROJECT_DROPDOWN}
          fill={true}
          icon={<AddIcon />}
          intent={Intent.NONE}
        />
      </HexPopover>
    </ButtonsContainer>
  );
});
